import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowLeft, Check, ChevronDown, Hammer, Info, Power, Square, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Composer from "@/components/chat/Composer";
import Markdown from "@/components/chat/Markdown";
import { useStickToBottom } from "@/components/chat/useStickToBottom";
import { Button, ErrorText, Input, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { useAgentLive, type AgentRecord, type AgentSession, type Block } from "@/lib/agents";
import { onEvent } from "@/lib/ws";
import { SessionStatus } from "./Agents";

interface Detail {
  session: AgentSession;
  events: AgentRecord[];
}

type ToolResult = { text: string; isError: boolean };

function textOf(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content.map((b) => (b?.type === "text" ? b.text : b?.type === "image" ? "[image]" : "")).join("");
}

function Thinking({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  if (!text.trim()) return null;
  return (
    <div className="my-1">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">
        Thinking <ChevronDown size={14} className={clsx("transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="mt-1 border-l-2 border-zinc-200 pl-3 text-sm whitespace-pre-wrap text-zinc-500 dark:border-zinc-800">{text.trim()}</div>}
    </div>
  );
}

function ToolCard({ name, args, result, running }: { name: string; args: unknown; result?: ToolResult; running: boolean }) {
  const [open, setOpen] = useState(false);
  const summary = typeof args === "object" && args ? String(Object.values(args as Record<string, unknown>)[0] ?? "").slice(0, 120) : "";
  return (
    <div className="my-1.5 overflow-hidden rounded-lg border border-zinc-200 text-sm dark:border-zinc-800">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-zinc-50 dark:hover:bg-zinc-900">
        <Hammer size={14} className="shrink-0 text-zinc-400" />
        <span className="font-medium">{name}</span>
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-zinc-500">{summary}</span>
        {running && !result ? <Spinner size={12} /> : result?.isError ? <X size={14} className="text-red-500" /> : <Check size={14} className="text-green-600" />}
      </button>
      {open && (
        <div className="space-y-2 border-t border-zinc-200 bg-zinc-50 p-2 dark:border-zinc-800 dark:bg-zinc-900/60">
          <pre className="max-h-48 overflow-auto rounded bg-white p-2 font-mono text-xs dark:bg-zinc-950">{JSON.stringify(args, null, 2)}</pre>
          {result && <pre className="max-h-80 overflow-auto rounded bg-white p-2 font-mono text-xs whitespace-pre-wrap dark:bg-zinc-950">{result.text || "(no output)"}</pre>}
        </div>
      )}
    </div>
  );
}

function AssistantBlocks({ blocks, results, running }: { blocks: Block[]; results: Map<string, ToolResult>; running: boolean }) {
  return (
    <div>
      {blocks.map((b, i) => {
        if (!b) return null;
        if (b.type === "thinking") return <Thinking key={i} text={b.thinking} />;
        if (b.type === "text") return b.text.trim() ? <Markdown key={i} text={b.text} streaming={running} /> : null;
        if (b.type === "toolCall") return <ToolCard key={i} name={b.name} args={b.arguments} result={results.get(b.id)} running />;
        return null;
      })}
    </div>
  );
}

function Dialog({ rec, answered, onAnswer }: { rec: AgentRecord; answered?: AgentRecord; onAnswer: (body: Record<string, unknown>) => void }) {
  const [value, setValue] = useState(String(rec.prefill ?? ""));
  return (
    <div className="my-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
      <p className="font-medium">{rec.title}</p>
      {rec.message && <p className="mt-1 whitespace-pre-wrap text-zinc-600 dark:text-zinc-300">{rec.message}</p>}
      {answered ? (
        <p className="mt-2 text-xs text-zinc-500">
          Answered: {answered.cancelled ? "cancelled" : answered.confirmed !== undefined ? (answered.confirmed ? "yes" : "no") : String(answered.value)}
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {rec.method === "confirm" && (
            <>
              <Button size="sm" onClick={() => onAnswer({ confirmed: true })}>
                Yes
              </Button>
              <Button size="sm" variant="secondary" onClick={() => onAnswer({ confirmed: false })}>
                No
              </Button>
            </>
          )}
          {rec.method === "select" &&
            (rec.options ?? []).map((o: string) => (
              <Button key={o} size="sm" variant="secondary" onClick={() => onAnswer({ value: o })}>
                {o}
              </Button>
            ))}
          {(rec.method === "input" || rec.method === "editor") && (
            <form
              className="flex w-full gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                onAnswer({ value });
              }}
            >
              <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={rec.placeholder} />
              <Button size="sm">Send</Button>
            </form>
          )}
          <Button size="sm" variant="ghost" onClick={() => onAnswer({ cancelled: true })}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AgentSessionPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["agent-session", id], queryFn: () => api.get<Detail>(`/api/agent-sessions/${id}`) });
  const partial = useAgentLive((s) => s.partial[id]);
  const [error, setError] = useState<unknown>(null);

  // Live records: deltas go to the partial message; anything stored triggers a reload.
  useEffect(() => {
    let timer: number | undefined;
    return onEvent((ev) => {
      if (ev.session_id !== id) return;
      if (ev.type === "agent.event") {
        const rec = ev.record as AgentRecord;
        useAgentLive.getState().apply(id, rec);
        if (rec.type !== "message_update" && rec.type !== "tool_execution_update") {
          window.clearTimeout(timer);
          timer = window.setTimeout(() => qc.invalidateQueries({ queryKey: ["agent-session", id] }), 120);
        }
      } else if (ev.type === "agent.status") {
        qc.invalidateQueries({ queryKey: ["agent-session", id] });
      }
    });
  }, [id, qc]);

  const events = useMemo(() => detail.data?.events ?? [], [detail.data]);
  const { results, answers } = useMemo(() => {
    const results = new Map<string, ToolResult>();
    const answers = new Map<string, AgentRecord>();
    for (const e of events) {
      if (e.type === "message_end" && e.message?.role === "toolResult") results.set(e.message.toolCallId, { text: textOf(e.message.content), isError: !!e.message.isError });
      if (e.type === "tool_execution_end" && !results.has(e.toolCallId)) results.set(e.toolCallId, { text: textOf(e.result?.content), isError: !!e.isError });
      if (e.type === "ui_response") answers.set(e.id, e);
    }
    return { results, answers };
  }, [events]);

  const s = detail.data?.session;
  const busy = s?.status === "busy" || s?.status === "starting";
  const ended = s?.status === "exited" || s?.status === "error";
  const { ref, toBottom } = useStickToBottom(`${events.length}:${JSON.stringify(partial?.blocks ?? []).length}`);

  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onError: setError,
    onSuccess: () => setError(null),
  });
  const send = (message: string, mode: "prompt" | "steer" | "follow_up") => (toBottom(), act.mutateAsync(() => api.post(`/api/agent-sessions/${id}/prompt`, { message, mode })));

  if (detail.isLoading)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  if (!s) return <ErrorText error={detail.error} />;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-zinc-200 px-4 pl-14 md:pl-4 dark:border-zinc-800">
        <Link to="/agents" className="rounded p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" aria-label="Back">
          <ArrowLeft size={16} />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{s.title || s.cwd}</p>
          <p className="truncate text-xs text-zinc-500">
            {s.kind} on {s.worker_name} · {s.cwd} {s.state.model ? `· ${String(s.state.model)}` : ""}
          </p>
        </div>
        <SessionStatus s={s} />
        {busy && (
          <Button size="sm" variant="secondary" onClick={() => act.mutate(() => api.post(`/api/agent-sessions/${id}/abort`))}>
            <Square size={12} /> Abort
          </Button>
        )}
        {!ended && (
          <Button size="sm" variant="ghost" onClick={() => confirm("Stop the agent process?") && act.mutate(() => api.post(`/api/agent-sessions/${id}/stop`))} title="Stop session">
            <Power size={14} />
          </Button>
        )}
      </header>
      <div ref={ref} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
          {events.length === 0 && !partial && <p className="text-center text-sm text-zinc-500">Send the agent a task to start.</p>}
          {events.map((e, i) => {
            if (e.type === "message_end") {
              const m = e.message ?? {};
              if (m.role === "user")
                return (
                  <div key={i} className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl bg-zinc-100 px-4 py-2.5 text-[15px] whitespace-pre-wrap dark:bg-zinc-800">{textOf(m.content)}</div>
                  </div>
                );
              if (m.role === "assistant") return <AssistantBlocks key={i} blocks={m.content ?? []} results={results} running={false} />;
              return null;
            }
            if (e.type === "extension_ui_request" && e.method !== "notify")
              return (
                <Dialog
                  key={i}
                  rec={e}
                  answered={answers.get(e.id)}
                  onAnswer={(body) => act.mutate(() => api.post(`/api/agent-sessions/${id}/ui-response`, { request_id: e.id, ...body }))}
                />
              );
            const note =
              e.type === "extension_ui_request" ? e.message : e.type === "session_note" ? e.text : e.type === "extension_error" ? `Extension error: ${e.error}` : e.type === "response" ? `${e.command} failed: ${e.error}` : null;
            if (note)
              return (
                <p key={i} className="flex items-start gap-1.5 text-xs whitespace-pre-wrap text-zinc-500">
                  <Info size={13} className="mt-0.5 shrink-0" /> {note}
                </p>
              );
            return null;
          })}
          {partial && <AssistantBlocks blocks={partial.blocks} results={results} running />}
          {busy && !partial && (
            <div className="flex h-6 items-center gap-1">
              <span className="h-2 w-2 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.3s]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.15s]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-zinc-400" />
            </div>
          )}
        </div>
      </div>
      <div className="mx-auto w-full max-w-3xl px-4 pb-4">
        <ErrorText error={error} />
        {ended ? (
          <p className="py-3 text-center text-sm text-zinc-500">This session has ended.</p>
        ) : !s.worker_online ? (
          <p className="py-3 text-center text-sm text-zinc-500">The worker is offline.</p>
        ) : busy ? (
          <BusyComposer onSend={send} />
        ) : (
          <Composer placeholder="Give the agent a task" onSend={async (text) => void (await send(text, "prompt"))} />
        )}
      </div>
    </div>
  );
}

function BusyComposer({ onSend }: { onSend: (text: string, mode: "steer" | "follow_up") => Promise<unknown> }) {
  const [mode, setMode] = useState<"steer" | "follow_up">("steer");
  return (
    <Composer
      placeholder={mode === "steer" ? "Steer: delivered after the current step" : "Follow-up: delivered when the agent is done"}
      onSend={async (text) => void (await onSend(text, mode))}
      toolbar={
        <select value={mode} onChange={(e) => setMode(e.target.value as "steer" | "follow_up")} className="rounded-lg bg-transparent py-1 text-sm outline-none hover:bg-zinc-100 dark:hover:bg-zinc-800">
          <option value="steer">Steer</option>
          <option value="follow_up">Follow-up</option>
        </select>
      }
    />
  );
}
